/**
 * The admin tier, fetched lazily from GET /admin — the admin root, what the
 * admin surface offers this caller (care-app/ADR-0015). The profile no longer
 * carries any of it, so the profile's availability and the tier's are
 * decoupled: a cold authorization store 503s this fetch alone, the boot never
 * waits on it, and the flags simply keep their previous value until a fetch
 * succeeds (fail-closed display; the surfaces re-enforce every permission).
 */
import { api } from '../api/client';
import { useAuthStore } from '@/stores/auth';
import { createLogger } from '@/utils/logger';

const logger = createLogger('AdminTier');

export interface AdminTier {
  isPlatformAdmin: boolean;
  adminView: boolean;
  adminViewTrialExpiresAt: string | null;
  canApprove: boolean;
  adminEndpoints: string[];
}

interface AdminTierResponse {
  is_platform_admin?: boolean;
  admin_view?: boolean;
  admin_view_trial_expires_at?: string;
  can_approve?: boolean;
  admin_endpoints?: string[];
}

/**
 * Fetch the caller's admin tier and store it on the auth user. Fire-and-
 * forget: a failure writes nothing, so a cold store never flips a real
 * admin's UI off (the same fail-closed contract the /me merge had).
 */
export async function refreshAdminTier(): Promise<void> {
  // Captured before the fetch: a late answer must not land on a newer
  // identity. On an identity swap (the OAuth callback's exchange, a
  // sibling-subdomain login) an older token's /admin can still be in
  // flight, and isPlatformAdmin is OR'd into the profile below — writing
  // it would graft the outgoing account's tier onto whoever is current,
  // keeping the admin nav lit for a non-admin all session. The same
  // only-if-still-active shape reconcileIdentity uses.
  const tokenAtFetch = useAuthStore.getState().accessToken;
  try {
    const raw = await api.get<AdminTierResponse>('/admin');
    const tier: AdminTier = {
      isPlatformAdmin: raw.is_platform_admin ?? false,
      adminView: raw.admin_view ?? false,
      adminViewTrialExpiresAt: raw.admin_view_trial_expires_at ?? null,
      canApprove: raw.can_approve ?? false,
      adminEndpoints: raw.admin_endpoints ?? [],
    };
    const { user, accessToken, setAuth } = useAuthStore.getState();
    if (user && accessToken && accessToken === tokenAtFetch) {
      setAuth(accessToken, {
        ...user,
        isPlatformAdmin: tier.isPlatformAdmin || user.isPlatformAdmin,
        adminView: tier.adminView,
        adminViewTrialExpiresAt: tier.adminViewTrialExpiresAt,
        canApprove: tier.canApprove,
        adminEndpoints: tier.adminEndpoints,
      });
    }
  } catch (err) {
    // No store write: the previous answer stands. A 503 carries Retry-After;
    // the api client's own cold-start retry already bridged once before this
    // catch sees it.
    logger.warn('admin tier unavailable; previous flags stand', { err });
  }
}
